import type { InputHTMLAttributes, Ref } from "react";

import { control } from "./styles";

/**
 * A text input that looks like the rest of the platform.
 *
 * Deliberately thin. It is still a real `<input>` - every native behaviour a
 * browser gives a text field is worth more than anything that could replace it,
 * and the only thing wrong with the native one was that it looked like 2009.
 * So this is styling plus one prop.
 */
export function TextInput({
  invalid = false,
  className = "",
  ref,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & {
  /** Draws the error border, and says so. The message belongs to `Field`. */
  invalid?: boolean;
  ref?: Ref<HTMLInputElement>;
}) {
  /*
   * The border is not the only thing that has to say this.
   *
   * `invalid` used to draw a red edge and nothing else, so a field the server
   * had rejected was announced by a screen reader as an ordinary empty box -
   * with `aria-describedby` pointing at an error message for a field that never
   * claimed to have one. Somebody who cannot see the border got no signal at
   * all that the form had come back with something wrong in it.
   *
   * `undefined` rather than `false` when it is fine, so the attribute is absent
   * on a valid field instead of present and saying "no".
   */
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={control(invalid, className)}
      {...rest}
    />
  );
}
